import type { Orientation } from './orientation';

// ─────────────────────────────────────────────────────────────────────────────────────────────────────────────
// I METADATI DI RIPRESA (ADR-031, WP1): per ogni scatto della campagna si registrano distanza, altezza della fotocamera,
// dove sta l'etichetta e se il carico è avvolto. Senza questi dati il rapporto del replay non può dire dove la misura funziona
// (per scenario e per distanza) e i casi non si confrontano fra loro. Più inclinazione e rollio, che arrivano dal sensore.
// Le unità stanno nei nomi (millimetri, gradi) come nel resto del formato; a schermo si scrive in metri e in centimetri.
// ─────────────────────────────────────────────────────────────────────────────────────────────────────────────

export type LabelPlacement = 'Front' | 'Top' | 'Absent';

export const LABEL_PLACEMENTS: readonly { value: LabelPlacement; label: string }[] = [
  { value: 'Front', label: 'Davanti' },
  { value: 'Top', label: 'Sopra' },
  { value: 'Absent', label: 'Assente' },
];

/** Ciò che chi scatta dichiara: nullo finché non l'ha detto. */
export interface ShotMeta {
  distanceMm: number | null;
  cameraHeightMm: number | null;
  labelPlacement: LabelPlacement | null;
  wrappedInFilm: boolean | null;
}

/** Ciò che resta registrato in ogni caso (blocco `capture` del formato 2.1). */
export interface CaptureMeta {
  distanceMm: number;
  cameraHeightMm: number;
  labelPlacement: LabelPlacement;
  wrappedInFilm: boolean;
  /** Dal sensore allo scatto; null se il dispositivo non ha il sensore di movimento. */
  pitchDownDeg: number | null;
  rollDeg: number | null;
}

/** Valori fisicamente sensati: un 25 scritto per «2,5 m» è un errore di battitura, non una distanza. Gli stessi limiti li controlla Dim.Tools alla lettura. */
export const PLAUSIBLE = {
  distanceMm: { min: 500, max: 8000 },
  cameraHeightMm: { min: 300, max: 2500 },
} as const;

export const DISTANCE_CHOICES_M = [1.5, 2, 2.5, 3, 3.5] as const;

function parseDecimal(text: string): number | null {
  const t = text.trim().replace(',', '.');
  if (t === '') return null;
  const v = Number(t);
  return Number.isFinite(v) ? v : null;
}

/** Metri scritti dall'operatore → millimetri interi; null se vuoto o non numerico. */
export function metresToMm(text: string): number | null {
  const v = parseDecimal(text);
  return v === null ? null : Math.round(v * 1000);
}

/** Centimetri scritti dall'operatore → millimetri interi; null se vuoto o non numerico. */
export function centimetresToMm(text: string): number | null {
  const v = parseDecimal(text);
  return v === null ? null : Math.round(v * 10);
}

/**
 * Un codice che comincia per CAL indica le viste della scacchiera per la calibrazione: non c'è un bancale, quindi niente distanza, etichetta o film da
 * chiedere prima di ogni scatto. La stessa regola sta in `BundleFiles.IsCalibrationCode` di Dim.Tools.
 */
export function isCalibrationCode(code: string): boolean {
  return /^cal/i.test(code.trim());
}

/** Ciò che manca o non è plausibile, in italiano per chi sta scattando; vuoto se si può scattare. */
export function checkMeta(m: ShotMeta): string[] {
  const problems: string[] = [];
  const mm = (v: number): string => (v / 1000).toLocaleString('it-IT');
  if (m.distanceMm === null) problems.push('distanza');
  else if (m.distanceMm < PLAUSIBLE.distanceMm.min || m.distanceMm > PLAUSIBLE.distanceMm.max) problems.push(`distanza (tra ${mm(PLAUSIBLE.distanceMm.min)} e ${mm(PLAUSIBLE.distanceMm.max)} m)`);
  if (m.cameraHeightMm === null) problems.push('altezza della pistola');
  else if (m.cameraHeightMm < PLAUSIBLE.cameraHeightMm.min || m.cameraHeightMm > PLAUSIBLE.cameraHeightMm.max) problems.push(`altezza della pistola (tra ${PLAUSIBLE.cameraHeightMm.min / 10} e ${PLAUSIBLE.cameraHeightMm.max / 10} cm)`);
  if (m.labelPlacement === null) problems.push('posizione dell’etichetta');
  if (m.wrappedInFilm === null) problems.push('film');
  return problems;
}

const round1 = (v: number): number => Math.round(v * 10) / 10;

/** Il blocco `capture` di un caso: i dati dichiarati più inclinazione e rollio del sensore. Rifiuta dati mancanti o non plausibili. */
export function buildCapture(m: ShotMeta, orientation: Orientation | null): CaptureMeta {
  const problems = checkMeta(m);
  if (problems.length > 0) throw new Error(`Metadati di ripresa incompleti: ${problems.join(', ')}`);
  return {
    distanceMm: m.distanceMm!,
    cameraHeightMm: m.cameraHeightMm!,
    labelPlacement: m.labelPlacement!,
    wrappedInFilm: m.wrappedInFilm!,
    pitchDownDeg: orientation ? round1(orientation.pitchDownDeg) : null,
    rollDeg: orientation ? round1(orientation.rollDeg) : null,
  };
}
